import { WORKFLOW_PRESET_RATIOS } from '@/pages/ipe/workbench/components/WorkflowAspectRatioField';
import {
  normalizeOutpaintCustomAspect,
} from '@/pages/ipe/workbench/utils/outpaintCustomAspectRatio';
import { clampOutpaintScale } from '@/pages/ipe/workbench/utils/outpaintExpandScale';

export const FISSION_RATIO_SIZE_PRESETS = {
  original: null,
  '1:1': { width: 1024, height: 1024 },
  '1:2': { width: 768, height: 1536 },
  '2:3': { width: 800, height: 1200 },
  '3:2': { width: 1200, height: 800 },
  '3:4': { width: 768, height: 1024 },
  '4:3': { width: 1024, height: 768 },
  '9:16': { width: 720, height: 1280 },
  '16:9': { width: 1280, height: 720 },
};

export const DEFAULT_FISSION_ASPECT_SIZE = {
  aspectRatio: '2:3',
  outputWidth: 800,
  outputHeight: 1200,
};

/** 灵图 expand · type=1 扩图倍数选项 */
export const OUTPAINT_SCALE_OPTIONS = [
  { value: 1.2, label: '1.2×' },
  { value: 1.25, label: '1.25×' },
  { value: 1.5, label: '1.5×' },
  { value: 2, label: '2×' },
  { value: 3, label: '3×' },
];

export const OUTPAINT_EXPAND_MODES = [
  { value: 1, label: '按原图倍数' },
  { value: 2, label: '按比例' },
];

/** 灵图 expand type=2 支持的 ar 预设（不含 1:2） */
export const OUTPAINT_AR_PRESETS = [
  { value: '1:1', w: 1, h: 1 },
  { value: '2:3', w: 2, h: 3 },
  { value: '3:2', w: 3, h: 2 },
  { value: '3:4', w: 3, h: 4 },
  { value: '4:3', w: 4, h: 3 },
  { value: '9:16', w: 9, h: 16 },
  { value: '16:9', w: 16, h: 9 },
];

export const OUTPAINT_AR_SIZE_PRESETS = {
  '1:1': { width: 1024, height: 1024 },
  '2:3': { width: 800, height: 1200 },
  '3:2': { width: 1200, height: 800 },
  '3:4': { width: 768, height: 1024 },
  '4:3': { width: 1024, height: 768 },
  '9:16': { width: 720, height: 1280 },
  '16:9': { width: 1280, height: 720 },
};

/** AI 扩图默认：type=1 按原图倍数 */
export const DEFAULT_OUTPAINT_EXPAND_PARAMS = {
  expandMode: 1,
  scale: 1.5,
  scaleCustom: false,
  aspectRatio: '1:1',
  outputWidth: 1024,
  outputHeight: 1024,
};

/** @deprecated 使用 DEFAULT_OUTPAINT_EXPAND_PARAMS */
export const DEFAULT_OUTPAINT_ASPECT_PARAMS = {
  aspectRatio: 'original',
  outputWidth: 0,
  outputHeight: 0,
};

export function getOutpaintPresetDimensions(ratio) {
  return OUTPAINT_AR_SIZE_PRESETS[ratio] || null;
}

export { WORKFLOW_PRESET_RATIOS };

export function getPresetRatioMeta(ratio) {
  const key = String(ratio || '');
  if (key === 'original' || key === 'custom') return null;
  return WORKFLOW_PRESET_RATIOS.find((item) => item.value === key) || null;
}

export function getPresetDimensions(ratio) {
  return FISSION_RATIO_SIZE_PRESETS[ratio] || null;
}

export function resolveRatioFraction(params) {
  const ratio = String(params?.aspectRatio || 'original');
  if (ratio === 'original') return null;
  const preset = getPresetRatioMeta(ratio);
  if (preset) return { w: preset.w, h: preset.h };
  const width = Math.max(1, Math.floor(Number(params?.outputWidth) || 0));
  const height = Math.max(1, Math.floor(Number(params?.outputHeight) || 0));
  if (width <= 0 || height <= 0) return null;
  return { w: width, h: height };
}

export function syncHeightByWidth(width, fraction) {
  const w = Math.max(1, Math.floor(Number(width) || 0));
  if (!fraction) return w;
  return Math.max(1, Math.round((w * fraction.h) / fraction.w));
}

export function syncWidthByHeight(height, fraction) {
  const h = Math.max(1, Math.floor(Number(height) || 0));
  if (!fraction) return h;
  return Math.max(1, Math.round((h * fraction.w) / fraction.h));
}

export function buildAspectSizeApiPayload(params) {
  const aspectRatio = String(params?.aspectRatio || 'original');
  const outputWidth = Math.max(1, Math.floor(Number(params?.outputWidth) || 0));
  const outputHeight = Math.max(1, Math.floor(Number(params?.outputHeight) || 0));
  const payload = {
    aspectRatio,
    outputWidth,
    outputHeight,
  };
  if (aspectRatio === 'custom') {
    payload.customAspectWidth = outputWidth;
    payload.customAspectHeight = outputHeight;
  }
  return payload;
}

/** AI 扩图：每张原图固定产出 1 张（不提供生成数量参数） */
export const OUTPAINT_OUTPUT_PER_SOURCE = 1;

/** AI 扩图提交体：映射灵图 expand 的 type / scale / ar */
export function buildOutpaintApiPayload({ expandParams, imageUrls, requestId }) {
  const mode = Number(expandParams?.expandMode) === 2 ? 2 : 1;
  const payload = {
    requestId,
    imageUrls: (imageUrls || []).filter(Boolean),
    outputCount: OUTPAINT_OUTPUT_PER_SOURCE,
    expandMode: mode,
  };
  if (mode === 2) {
    const aspectRatio = String(expandParams?.aspectRatio || '1:1');
    payload.aspectRatio = aspectRatio;
    let outputWidth = Math.max(1, Math.floor(Number(expandParams?.outputWidth) || 0));
    let outputHeight = Math.max(1, Math.floor(Number(expandParams?.outputHeight) || 0));
    if (aspectRatio === 'custom' && outputWidth > 0 && outputHeight > 0) {
      const normalized = normalizeOutpaintCustomAspect(outputWidth, outputHeight);
      outputWidth = normalized.outputWidth;
      outputHeight = normalized.outputHeight;
    }
    payload.outputWidth = outputWidth;
    payload.outputHeight = outputHeight;
    if (aspectRatio === 'custom' && payload.outputWidth > 0 && payload.outputHeight > 0) {
      payload.customAspectWidth = payload.outputWidth;
      payload.customAspectHeight = payload.outputHeight;
    }
  } else {
    payload.aspectRatio = 'original';
    payload.scale = clampOutpaintScale(expandParams?.scale);
  }
  return payload;
}
